import { Dialog, DialogContent, DialogFooter, DialogTitle } from "../ui/dialog";
import { useTranslation } from "react-i18next";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Textarea } from "../ui/textarea";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { Button } from "../ui/button";
import { cn } from "@/lib/utils";
import { Ensemble } from "@/lib/types";
import { useEffect } from "react";

const schema = z.object({
  ensemblesDescriptionNomEnsemble: z.string().min(1),
  ensemblesDescriptionNotesEnsemble: z.string(),
  visibility: z.enum(["private", "shared"]),
});

export type NewArtworkSetFormValues = z.infer<typeof schema>;

type NewArtworkSetModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: NewArtworkSetFormValues) => void;
  setToUpdate?: Ensemble;
};

const NewArtworkSetModal = ({
  onOpenChange,
  open,
  onSubmit,
  setToUpdate,
}: NewArtworkSetModalProps) => {
  const { t } = useTranslation();
  const {
    handleSubmit,
    register,
    formState: { errors, isDirty, isValid },
    watch,
    reset,
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      ensemblesDescriptionNomEnsemble:
        setToUpdate?.ensemblesDescriptionNomEnsemble ?? "",
      ensemblesDescriptionNotesEnsemble:
        setToUpdate?.ensemblesDescriptionNotesEnsemble ?? "",
      visibility: setToUpdate?.visibility ?? "shared",
    },
  });

  useEffect(() => {
    if (setToUpdate && open) {
      const {
        ensemblesDescriptionNomEnsemble,
        ensemblesDescriptionNotesEnsemble,
        visibility,
      } = setToUpdate;
      reset({
        ensemblesDescriptionNomEnsemble,
        ensemblesDescriptionNotesEnsemble,
        visibility,
      });
    }
  }, [open, setToUpdate, reset]);

  const onFormSubmit = handleSubmit((data) => {
    onSubmit(data as NewArtworkSetFormValues);
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="p-0 bg-white">
        <DialogTitle className="font-semibold text-lg text-center px-8 pt-8">
          {t(setToUpdate ? "updateEnsemble" : "createNewEnsemble")}
        </DialogTitle>

        <form onSubmit={onFormSubmit} className="space-y-4">
          <div className="space-y-2 px-8">
            <Label>{t("ensemblesDescriptionNomEnsemble")}</Label>
            <Input
              {...register("ensemblesDescriptionNomEnsemble")}
              className={
                errors.ensemblesDescriptionNomEnsemble ? "border-red-500" : ""
              }
            />
            {errors.ensemblesDescriptionNomEnsemble && (
              <p className="text-sm text-red-500">
                {errors.ensemblesDescriptionNomEnsemble.message}
              </p>
            )}
          </div>

          <div className="space-y-2 px-8">
            <Label>{t("ensemblesDescriptionNotesEnsemble")}</Label>
            <Textarea
              {...register("ensemblesDescriptionNotesEnsemble")}
              className="min-h-[100px]"
            />
          </div>

          <div className="space-y-2 px-8 pb-6">
            <Label>{t("ensembleVisibility")}</Label>
            <RadioGroup
              defaultValue="shared"
              value={watch("visibility")}
              onValueChange={(value) => {
                register("visibility").onChange({
                  target: { name: "visibility", value },
                });
              }}
              className="flex flex-row gap-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="shared" id="shared" />
                <Label htmlFor="shared">{t("shared")}</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="private" id="private" />
                <Label htmlFor="private">{t("private")}</Label>
              </div>
            </RadioGroup>
          </div>

          <DialogFooter
            className={cn(
              "flex flex-row align-center gap-4 border-t p-6 bg-white",
              "relative before:absolute before:top-[-12px] before:inset-x-0",
              "before:h-3 before:bg-gradient-to-t before:from-black/5 before:to-transparent",
            )}
          >
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex flex-1 bg-white"
            >
              {t("actionCancel")}
            </Button>
            <Button
              disabled={!isDirty || !isValid}
              type="submit"
              className="flex flex-1"
            >
              {setToUpdate ? t("formUpdate") : t("addKey")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default NewArtworkSetModal;
